import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import FundingPanel from '../../components/funding/FundingPanel';
import FundingReadinessBlockers from '../../components/funding/FundingReadinessBlockers';
import FundingReadinessBreakdown from '../../components/funding/FundingReadinessBreakdown';
import FundingReadinessRecommendations from '../../components/funding/FundingReadinessRecommendations';
import FundingReadinessScoreCard, { FundingReadinessTierBadge } from '../../components/funding/FundingReadinessScoreCard';
import FundingSectionPage from '../../components/funding/FundingSectionPage';
import FundingAIAdvisorActionLink from '../../components/funding/FundingAIAdvisorActionLink';
import { useFundingWorkspace } from '../../context/FundingWorkspaceContext';
import { useFundingReadiness, useFundingReadinessList } from '../../hooks/funding/useFundingReadiness';
import { fetchFundingClients } from '../../services/funding/fundingClientsService';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const FundingReadinessPage = () => {
  const { workspaceAgencyId, isPlatformContext, requiresAgencySelection } = useFundingWorkspace();
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState('');

  const selectedClient = useMemo(
    () => clients.find((client) => client.id === selectedClientId) || null,
    [clients, selectedClientId],
  );

  const agencyIdForClient = selectedClient?.agencyId || workspaceAgencyId || undefined;

  const {
    check,
    loading: readinessLoading,
    running,
    error: readinessError,
    run,
  } = useFundingReadiness({
    clientId: selectedClientId,
    agencyId: agencyIdForClient,
    autoLoad: Boolean(selectedClientId && agencyIdForClient),
  });

  const {
    checks: recentChecks,
    loading: recentLoading,
    reload: reloadRecent,
  } = useFundingReadinessList({
    agencyId: isPlatformContext ? workspaceAgencyId || undefined : workspaceAgencyId,
    limit: 20,
  });

  const loadClients = useCallback(async () => {
    if (!isPlatformContext && !workspaceAgencyId) {
      setClients([]);
      setClientsLoading(false);
      return;
    }

    setClientsLoading(true);
    try {
      const result = await fetchFundingClients({
        agencyId: isPlatformContext ? workspaceAgencyId || undefined : workspaceAgencyId,
      });
      setClients(result.clients);
      if (!selectedClientId && result.clients.length > 0) {
        setSelectedClientId(result.clients[0].id);
      }
    } catch (err) {
      setClients([]);
    } finally {
      setClientsLoading(false);
    }
  }, [isPlatformContext, workspaceAgencyId, selectedClientId]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const handleRunReadiness = async () => {
    if (!selectedClientId || !agencyIdForClient) return;
    try {
      await run();
      await reloadRecent();
      await loadClients();
    } catch {
      // error surfaced in hook
    }
  };

  return (
    <FundingSectionPage
      title="Readiness"
      subtitle="Estimate credit, business, revenue, document, and funding fit readiness before lender matching."
      activeStage="readiness"
      actions={(
        <div className="flex gap-2">
          <FundingAIAdvisorActionLink
            label="AI Advisor"
            clientId={selectedClientId || undefined}
            prompt="Improve readiness"
          />
          <Button
          className="rounded-xl bg-[var(--fg-accent)] hover:bg-[#126b4f]"
          onClick={handleRunReadiness}
          disabled={!selectedClientId || !agencyIdForClient || running}
          data-testid="funding-readiness-run"
        >
          {running ? 'Running readiness...' : 'Run Readiness'}
        </Button>
        </div>
      )}
    >
      <div className="grid gap-4 xl:grid-cols-[320px_1fr]">
        <FundingPanel title="Funding client" subtitle="Select a client to review readiness">
          {requiresAgencySelection ? (
            <p className="text-sm text-[var(--fg-muted)]">
              Select an agency in the header to load funding clients.
            </p>
          ) : clientsLoading ? (
            <p className="text-sm text-[var(--fg-muted)]">Loading clients...</p>
          ) : clients.length === 0 ? (
            <p className="text-sm text-[var(--fg-muted)]">
              Create a funding client first, then run readiness scoring from this workspace.
            </p>
          ) : (
            <select
              value={selectedClientId}
              onChange={(event) => setSelectedClientId(event.target.value)}
              className="w-full rounded-xl border border-[var(--fg-line)] bg-white px-3 py-2.5 text-sm text-[var(--fg-text)] outline-none ring-[var(--fg-accent)] focus:ring-2 dark:bg-[rgba(11,31,24,0.65)]"
              data-testid="funding-readiness-client-select"
            >
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                  {client.readinessScore != null ? ` — ${client.readinessScore}%` : ''}
                </option>
              ))}
            </select>
          )}

          {selectedClient ? (
            <div className="mt-4 space-y-2 rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] p-3 text-sm">
              <div className="font-medium text-[var(--fg-text)]">{selectedClient.businessName || selectedClient.name}</div>
              <div className="text-[var(--fg-muted)]">{selectedClient.email || 'No email on file'}</div>
              <div className="flex items-center gap-2">
                {selectedClient.readinessTier ? (
                  <FundingReadinessTierBadge tier={selectedClient.readinessTier} />
                ) : null}
                {selectedClient.readinessScore != null ? (
                  <span className="text-xs text-[var(--fg-muted)]">{selectedClient.readinessScore}% estimated</span>
                ) : null}
              </div>
            </div>
          ) : null}
        </FundingPanel>

        <div className="space-y-4">
          {readinessError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
              {readinessError}
            </div>
          ) : null}

          <FundingReadinessScoreCard check={check} loading={readinessLoading && !check} />

          <div className="grid gap-4 lg:grid-cols-2">
            <FundingPanel title="Category breakdown" subtitle="Five readiness dimensions">
              <FundingReadinessBreakdown check={check} />
            </FundingPanel>

            <div className="space-y-4">
              <FundingReadinessBlockers blockers={check?.blockers} />
              <FundingReadinessRecommendations recommendations={check?.recommendations} />
            </div>
          </div>

          {check?.missingFields?.length ? (
            <FundingPanel title="Missing fields" subtitle="Intake gaps affecting this estimate">
              <div className="flex flex-wrap gap-2">
                {check.missingFields.map((field) => (
                  <span
                    key={field}
                    className="rounded-full border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-3 py-1 text-xs font-medium text-[var(--fg-muted)]"
                  >
                    {field}
                  </span>
                ))}
              </div>
            </FundingPanel>
          ) : null}
        </div>
      </div>

      <FundingPanel title="Recent readiness checks" subtitle="Latest estimates across this workspace">
        {recentLoading ? (
          <p className="text-sm text-[var(--fg-muted)]">Loading recent checks...</p>
        ) : recentChecks.length === 0 ? (
          <p className="text-sm text-[var(--fg-muted)]">
            No readiness checks yet. Run readiness on a funding client to populate this list.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[var(--fg-line)] text-[var(--fg-muted)]">
                  <th className="px-3 py-2 font-medium">Client</th>
                  <th className="px-3 py-2 font-medium">Score</th>
                  <th className="px-3 py-2 font-medium">Tier</th>
                  <th className="px-3 py-2 font-medium">Generated</th>
                </tr>
              </thead>
              <tbody>
                {recentChecks.map((item) => (
                  <tr key={item.id} className="border-b border-[var(--fg-line)]/70">
                    <td className="px-3 py-3 text-[var(--fg-text)]">{item.clientName || item.fundingClientId}</td>
                    <td className="px-3 py-3 text-[var(--fg-text)]">{item.totalScore}%</td>
                    <td className="px-3 py-3">
                      <FundingReadinessTierBadge tier={item.tier} />
                    </td>
                    <td className="px-3 py-3 text-[var(--fg-muted)]">{formatDate(item.generatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </FundingPanel>
    </FundingSectionPage>
  );
};

export default FundingReadinessPage;
